import React, { useState } from "react";
import api from "../lib/api";
import { useToast, toastErrorMessage } from "../lib/toast";
import { Download, Upload, AlertTriangle, Cloud } from "lucide-react";

const RESTORE_PHRASE = "RESTORE-CESCO-EQUIPMENT";

/**
 * Admin-only full-database backup + restore panel.
 * - Downloads a single JSON file containing every persistent business collection.
 * - Restores from the same JSON with a hard confirmation phrase.
 * - Manually triggers the daily Google Drive upload (proves the scheduler works).
 * This exists because external MongoDB connections to production are disabled.
 */
export default function BackupRestorePanel() {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const [pushingDrive, setPushingDrive] = useState(false);
  const [driveResult, setDriveResult] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);

  async function downloadBackup() {
    setDownloading(true);
    try {
      const res = await api.get("/admin/backup.json", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url; a.download = `cesco-crm-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(toastErrorMessage(e));
    } finally { setDownloading(false); }
  }

  async function pushToDrive() {
    setPushingDrive(true);
    setDriveResult(null);
    try {
      const { data } = await api.post("/admin/backup-to-drive");
      setDriveResult(data);
      toast.success(`Uploaded to Google Drive - ${data.uploaded.name}`);
    } catch (e) {
      toast.error(toastErrorMessage(e));
    } finally { setPushingDrive(false); }
  }

  async function executeRestore() {
    if (!restoreFile) return;
    setRestoring(true);
    try {
      const form = new FormData();
      form.append("file", restoreFile);
      form.append("confirmation", RESTORE_PHRASE);
      const { data } = await api.post("/admin/restore", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setConfirmOpen(false);
      setPhrase(""); setRestoreFile(null);
      const total = Object.values(data.restored).reduce((a, b) => a + b, 0);
      toast.success(`Restored ${total} documents across ${Object.keys(data.restored).length} collections`);
    } catch (e) {
      toast.error(toastErrorMessage(e));
    } finally { setRestoring(false); }
  }

  const canStart = restoreFile && phrase === RESTORE_PHRASE;

  return (
    <div className="cesco-card p-5 border-blue-200 space-y-5" data-testid="backup-restore-panel">
      <div>
        <h2 className="text-lg font-semibold">Full database backup &amp; restore</h2>
        <p className="text-sm text-gray-600 mt-1">
          One-file snapshot of every company, contact, quote, equipment record, discovery entry, activity, quote-number counter and user (with password hashes).
          <b> Use this if outside connections to the database are not available.</b>
        </p>
      </div>

      {/* Download */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-medium">Download backup</div>
          <div className="text-xs text-gray-500">Produces a single <code>.json</code> file (typically 100 KB - a few MB). Store somewhere safe.</div>
        </div>
        <button className="cesco-btn-primary flex items-center gap-2" onClick={downloadBackup} disabled={downloading}
                data-testid="backup-download-btn">
          <Download size={14}/> {downloading ? "Preparing..." : "Download backup"}
        </button>
      </div>

      <hr />

      {/* Push to Google Drive (uses the same scheduled cron path) */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-medium flex items-center gap-2"><Cloud size={16}/> Push backup to Google Drive</div>
          <div className="text-xs text-gray-500">
            Runs the same job the scheduler fires nightly at 02:00 AEST. Requires
            <code className="bg-gray-100 px-1 rounded mx-1">GDRIVE_SERVICE_ACCOUNT_JSON</code>
            and <code className="bg-gray-100 px-1 rounded mx-1">GDRIVE_BACKUP_FOLDER_ID</code> env vars.
          </div>
        </div>
        <button className="cesco-btn-primary flex items-center gap-2" onClick={pushToDrive} disabled={pushingDrive}
                data-testid="backup-drive-btn">
          <Cloud size={14}/> {pushingDrive ? "Uploading..." : "Push to Drive now"}
        </button>
      </div>
      {driveResult && (
        <div className="text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded p-2" data-testid="backup-drive-result">
          Uploaded <b>{driveResult.uploaded.name}</b> ({(Number(driveResult.uploaded.size) / 1024).toFixed(1)} KB).
          {" "}
          {driveResult.uploaded.link && (
            <a href={driveResult.uploaded.link} target="_blank" rel="noreferrer" className="text-blue-700 underline">Open in Drive</a>
          )}
          <div className="mt-1 text-gray-500">
            Retention pass: kept {driveResult.retention.kept}, deleted {driveResult.retention.deleted.length}.
          </div>
        </div>
      )}

      <hr />

      {/* Restore */}
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5"/>
          <div>
            <div className="font-medium text-red-700">Restore from backup</div>
            <div className="text-xs text-gray-600">
              <b>Destructive.</b> Wipes companies, contacts, activities, discovery entries, quotes, equipment, quote counter and users, then repopulates from the file.
              Your current admin session survives because your user record is restored (with your password hash) rather than dropped.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="cesco-label">Backup file (.json)</label>
            <input type="file" accept="application/json,.json"
                   className="cesco-input"
                   onChange={e => { setRestoreFile(e.target.files?.[0] || null); setResult(null); }}
                   data-testid="backup-file-input"/>
          </div>
          <div>
            <label className="cesco-label">
              Type <code className="bg-gray-100 px-1 rounded">{RESTORE_PHRASE}</code> to enable
            </label>
            <input className="cesco-input font-mono" value={phrase}
                   onChange={e => setPhrase(e.target.value)}
                   placeholder={RESTORE_PHRASE}
                   data-testid="backup-phrase-input"/>
          </div>
        </div>
        <button
          className={`cesco-btn-primary flex items-center gap-2 ${canStart ? "!bg-red-600 hover:!bg-red-700" : "opacity-50 cursor-not-allowed"}`}
          disabled={!canStart || restoring}
          onClick={() => setConfirmOpen(true)}
          data-testid="backup-restore-btn"
        >
          <Upload size={14}/> {restoring ? "Restoring..." : "Restore from this file"}
        </button>
      </div>

      {result && (
        <div className="mt-2 text-sm" data-testid="backup-restore-result">
          <div className="font-semibold text-green-700 mb-1">Restore complete.</div>
          <div className="text-xs text-gray-600 mb-2">
            Backup taken {result.source_exported_at || "-"} by {result.source_exported_by || "-"}
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs font-semibold border-b pb-1">
            <span>Collection</span><span>Wiped</span><span>Restored</span>
          </div>
          {Object.keys(result.restored).map((k) => (
            <div key={k} className="grid grid-cols-3 gap-2 py-1 border-b border-gray-100 text-xs">
              <span className="text-gray-700">{k}</span>
              <span className="text-red-600">-{result.wiped[k] || 0}</span>
              <span className="text-green-700">+{result.restored[k]}</span>
            </div>
          ))}
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md space-y-4">
            <h3 className="text-lg font-bold text-red-700 flex items-center gap-2">
              <AlertTriangle/> Last chance
            </h3>
            <p className="text-sm">
              About to <b>wipe every business record</b> and reload from <code>{restoreFile?.name}</code>.
              This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button className="cesco-btn-ghost" onClick={() => setConfirmOpen(false)} disabled={restoring}>Cancel</button>
              <button className="cesco-btn-primary !bg-red-600 hover:!bg-red-700"
                      onClick={executeRestore} disabled={restoring}
                      data-testid="backup-restore-confirm-btn">
                {restoring ? "Restoring..." : "Yes - restore now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
